import { RefObject, useEffect } from "react";

import BaseErrors from "@components/fields/BaseErrors";
import RichTextarea from "@components/fields/RichTextarea";
import styles from "@components/forms/ExpertMessageForm.module.css";
import Form from "@components/forms/Form";
import { InboxContext } from "@components/messaging/InboxProvider";
import MessageAttachment from "@components/messaging/MessageAttachment";
import useContextSafely from "@hooks/useContextSafely";
import useFormPersist from "@hooks/useFormPersist";
import useMessageForm from "@hooks/useMessageForm";
import { GetJourneyForMessagesQuery } from "@typing/Generated";
import { standardRichTextareaMenuControls } from "@utils/formUtils";

type FormData = {
  body: string;
  directUploadBlobIds?: string[];
};

type Journey = GetJourneyForMessagesQuery["journey"];
type Chat = Journey["chats"][number];

type Props = {
  chat: Chat;
  contentRef?: RefObject<HTMLIonContentElement>;
  onMessageCreate: () => void;
};

const ExpertMessageForm = ({ chat, contentRef, onMessageCreate }: Props) => {
  const { baseErrors, editorRefresher, form, handleUploadsChange, onSubmit, placeholder, setTemplateId, uploadsWatch } =
    useMessageForm({
      chat,
      contentRef,
      onMessageCreate
    });

  const { journey, resourceData, setResourceData, setWorksheetData, worksheetData } = useContextSafely(InboxContext);

  const formPersist = useFormPersist("messageForm", {
    form,
    onDataRestored: () => {
      editorRefresher.toggle();
    },
    uniqueId: `${journey.id}-${chat.id}`
  });

  useEffect(() => {
    form.formState.isSubmitSuccessful && formPersist.clear();
  }, [form.formState, formPersist]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      {resourceData && (
        <div className="margin-5">
          <MessageAttachment handleRemoveRecord={() => setResourceData(undefined)} record={resourceData} />
        </div>
      )}
      {worksheetData && (
        <div className="margin-5">
          <MessageAttachment handleRemoveRecord={() => setWorksheetData(undefined)} record={worksheetData} />
        </div>
      )}
      <BaseErrors errors={baseErrors} />
      <div className={styles.container}>
        <div className="full-width">
          <RichTextarea<FormData>
            controls={[
              ...standardRichTextareaMenuControls.filter(c => c !== "headings"),
              "attachments",
              "inAppLinks",
              "submit",
              "templates"
            ]}
            editorRefresher={editorRefresher}
            error={form.formState.errors.body}
            form={form}
            journey={journey}
            journeyId={journey.id}
            name="body"
            onUploadsChange={handleUploadsChange}
            placeholder={placeholder}
            setTemplateId={setTemplateId}
            size="small"
            submitDisabled={!form.formState.isValid || form.formState.isSubmitting}
            submitVariant="send"
            uploadsWatch={uploadsWatch}
          />
        </div>
      </div>
    </Form>
  );
};

export default ExpertMessageForm;
